<template>
	<div class="g-rich-text-editable" :class="{ 'g-rich-text-editable--fit-content': fitContent }">
		<div
			ref="editor"
			class="g-rich-text-editable__content"
			contenteditable="true"
			:data-placeholder="placeholder"
			@input="onInput"
			@focus="onFocus"
			@click.stop="onFocus"
			@mouseup="onSelectionChange"
			@keyup="onSelectionChange"
			@blur="onBlur"
		></div>
		<Teleport to="body">
			<SelectionTextToolbar
				v-if="toolbarVisible"
				ref="toolbar"
				:position-style="toolbarStyle"
				:states="formatStates"
				@format="onFormat"
				@text-color="onTextColor"
				@bg-color="onBgColor"
				@font-size="onFontSize"
				@align="onAlign"
			/>
		</Teleport>
	</div>
</template>

<script>
	import SelectionTextToolbar from './SelectionTextToolbar.vue';
	import {
		execBackgroundColor,
		execFontSize,
		execFormat,
		execTextAlign,
		execTextColor,
		getSelectionRect,
		queryFormatState,
	} from '../utils/richText';

	export default {
		name: 'GRichTextEditable',
		components: { SelectionTextToolbar },
		props: {
			modelValue: {
				type: String,
				default: '',
			},
			placeholder: {
				type: String,
				default: '',
			},
			fitContent: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['update:modelValue', 'focus'],
		data() {
			return {
				toolbarVisible: false,
				toolbarStyle: {},
				formatStates: {
					bold: false,
					italic: false,
					underline: false,
					fontSize: null,
					textAlign: 'left',
				},
				savedRange: null,
				toolbarInteracting: false,
			};
		},
		watch: {
			modelValue(val) {
				const editor = this.$refs.editor;
				if (!editor || editor.innerHTML === val) return;
				editor.innerHTML = val || '';
				this.autoResize();
			},
		},
		mounted() {
			const editor = this.$refs.editor;
			if (editor) {
				editor.innerHTML = this.modelValue || '';
				this.autoResize();
			}
			document.addEventListener('mousedown', this.onDocumentMouseDown);
			document.addEventListener('selectionchange', this.onDocumentSelectionChange);
		},
		beforeUnmount() {
			document.removeEventListener('mousedown', this.onDocumentMouseDown);
			document.removeEventListener('selectionchange', this.onDocumentSelectionChange);
		},
		methods: {
			focus() {
				const editor = this.$refs.editor;
				if (!editor) return;
				editor.focus({ preventScroll: true });
				const selection = window.getSelection();
				if (!selection) return;
				const range = document.createRange();
				range.selectNodeContents(editor);
				range.collapse(false);
				selection.removeAllRanges();
				selection.addRange(range);
			},
			onFocus() {
				this.$emit('focus');
			},
			onInput() {
				this.syncContent();
				this.autoResize();
			},
			syncContent() {
				const html = this.$refs.editor?.innerHTML || '';
				const normalized = html === '<br>' ? '' : html;
				this.$emit('update:modelValue', normalized);
			},
			autoResize() {
				const el = this.$refs.editor;
				if (!el) return;
				if (this.fitContent) {
					el.style.height = 'auto';
					el.style.minHeight = '';
					return;
				}
				el.style.height = 'auto';
				el.style.height = `${el.scrollHeight}px`;
			},
			saveSelection() {
				const selection = window.getSelection();
				if (!selection || selection.rangeCount === 0) return;
				this.savedRange = selection.getRangeAt(0).cloneRange();
			},
			restoreSelection() {
				if (!this.savedRange) return;
				const selection = window.getSelection();
				selection.removeAllRanges();
				selection.addRange(this.savedRange);
			},
			onSelectionChange() {
				this.$nextTick(() => {
					this.updateToolbar();
				});
			},
			onDocumentSelectionChange() {
				this.$nextTick(() => {
					this.updateToolbar();
				});
			},
			updateToolbar() {
				const editor = this.$refs.editor;
				if (!editor) return;

				if (this.shouldKeepToolbar()) {
					this.toolbarVisible = true;
					return;
				}

				const rect = getSelectionRect(editor);
				if (!rect || (rect.width === 0 && rect.height === 0)) {
					this.toolbarVisible = false;
					return;
				}

				this.saveSelection();
				this.formatStates = queryFormatState();
				this.toolbarVisible = true;

				this.$nextTick(() => {
					const toolbarEl = this.$refs.toolbar?.$el;
					const toolbarHeight = toolbarEl?.offsetHeight || 48;
					const gap = 10;
					this.toolbarStyle = {
						top: `${Math.max(8, rect.top - toolbarHeight - gap)}px`,
						left: `${rect.left + rect.width / 2}px`,
					};
				});
			},
			shouldKeepToolbar() {
				return !!this.$refs.toolbar?.hasOpenPanel?.();
			},
			onDocumentMouseDown(e) {
				const toolbarEl = this.$refs.toolbar?.$el;
				if (toolbarEl?.contains(e.target)) {
					this.toolbarInteracting = true;
					return;
				}

				this.toolbarInteracting = false;

				const editor = this.$refs.editor;
				if (editor?.contains(e.target)) {
					return;
				}

				this.toolbarVisible = false;
				this.$refs.toolbar?.closePanel?.();
			},
			onBlur() {
				setTimeout(() => {
					if (this.shouldKeepToolbar() || this.toolbarInteracting) {
						this.toolbarInteracting = false;
						return;
					}
					this.toolbarVisible = false;
					this.toolbarInteracting = false;
				}, 150);
			},
			applyCommand(fn) {
				this.$refs.editor?.focus();
				this.restoreSelection();
				fn();
				this.formatStates = queryFormatState();
				this.syncContent();
				this.autoResize();
				this.saveSelection();
				this.$nextTick(() => {
					this.updateToolbar();
				});
			},
			onFormat(type) {
				const commandMap = {
					bold: 'bold',
					italic: 'italic',
					underline: 'underline',
				};
				this.applyCommand(() => execFormat(commandMap[type]));
			},
			onTextColor(color) {
				this.applyCommand(() => execTextColor(color));
			},
			onBgColor(color) {
				this.applyCommand(() => execBackgroundColor(color));
			},
			onFontSize(size) {
				this.applyCommand(() => execFontSize(size));
			},
			onAlign(align) {
				this.applyCommand(() => execTextAlign(align));
			},
		},
	};
</script>

<style scoped lang="less">
	.g-rich-text-editable {
		width: 100%;
	}

	.g-rich-text-editable__content {
		display: block;
		width: 100%;
		min-height: 1.5em;
		border: none;
		outline: none;
		background: transparent;
		word-break: break-word;
		white-space: pre-wrap;
		box-sizing: border-box;

		&:empty::before {
			content: attr(data-placeholder);
			color: #d9d9d9;
			pointer-events: none;
		}
	}

	.g-rich-text-editable--fit-content {
		width: auto;
		max-width: 100%;

		.g-rich-text-editable__content {
			display: inline-block;
			width: auto;
			max-width: 100%;
			min-height: unset;
			height: auto;
			line-height: 1.2;
			vertical-align: middle;
		}
	}
</style>
